import { Supabase, SupabaseClient, SupabaseLib } from 'app/services/supabase';
import { LogicModel } from './logicModel';
import { IController } from './types';
import { Disposer } from '@zajno/common/functions/disposer';
import { EdgeFunctions } from 'common/types/api';
import type { FunctionInvokeOptions } from '@supabase/functions-js';

export abstract class BaseController extends LogicModel implements IController {

    private _supabase: SupabaseClient = null;
    private _supabaseLib: SupabaseLib = null;

    protected readonly disposer = new Disposer();

    protected get supabase() { return this._supabase; }

    public async initialize() {
        this.dispose();
        await this.runAction(async () => {
            const { client, lib } = await Supabase.promise;
            this._supabase = client;
            this._supabaseLib = lib;
            await this.onInitialize();
        }, 'initialize');
        return this;
    }

    protected onInitialize(): void | Promise<void> {
        // no-op
    }

    protected async invokeFunction<TType extends EdgeFunctions.Types>(name: TType, body: EdgeFunctions.Input<TType>, options?: Omit<FunctionInvokeOptions, 'body'>): Promise<EdgeFunctions.Output<TType>> {
        const resp = await this.supabase.functions.invoke(name, { body, ...options });
        await this.checkFunctionErrors(name, resp.error);
        return resp.data;
    }

    private async checkFunctionErrors(name: string, error: any) {
        if (!error) {
            return;
        }

        if (error instanceof this._supabaseLib.FunctionsHttpError) {
            const errorData = await error.context.json();
            throw new Error(`Function "${name}" invocation error`, { cause: errorData });
        }

        if (error instanceof this._supabaseLib.FunctionsRelayError) {
            this.logger.error('Relay error:', error.message);
        } else if (error instanceof this._supabaseLib.FunctionsFetchError) {
            this.logger.error('Fetch error:', error.message);
        }

        throw error;
    }

    /** will be called on initialize! */
    public dispose() {
        this.disposer.dispose();
    }
}
